import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboard, getOrderClients } from '../services/orders';
import './DashboardPage.css';

/** LAKPED-16.7 — el panel de seguimiento.
 *
 *  Enseña solo lo que se puede calcular con datos nuestros. La desviación del prototipo era contra
 *  el importe del albarán, que vendría de ProSicar: aquí se compara lo ofertado con lo que acabó
 *  valiendo el pedido.
 */
const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const euros = (valor) => Number(valor || 0).toLocaleString('es-ES', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
});
const horas = (valor) => `${Number(valor || 0).toLocaleString('es-ES', { maximumFractionDigits: 0 })} h`;
// En el top de piezas los números son pequeños: redondear a enteros convierte 8,75 h en «9 h».
const horasFinas = (valor) => `${Number(valor || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`;
const porcentaje = (valor) => (valor === null || valor === undefined
  ? '—'
  : `${valor > 0 ? '+' : ''}${String(valor).replace('.', ',')} %`);

const DashboardPage = () => {
  const navigate = useNavigate();
  const anioActual = new Date().getFullYear();
  const [anio, setAnio] = useState(anioActual);
  const [clienteHash, setClienteHash] = useState('');
  const [clientes, setClientes] = useState([]);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { getOrderClients().then(setClientes).catch(() => setClientes([])); }, []);

  const cargar = useCallback(async () => {
    try {
      setDatos(await getDashboard({ year: anio, clientHash: clienteHash || null }));
      setError(null);
    } catch (err) {
      setError('No se ha podido cargar el panel.');
    }
  }, [anio, clienteHash]);

  useEffect(() => { cargar(); }, [cargar]);

  if (error) return <div className="panel"><p className="panel-error">{error}</p></div>;
  if (!datos) return <div className="panel"><p>Cargando…</p></div>;

  const r = datos.resumen;
  const topHoras = Math.max(
    ...datos.meses.map((m) => Math.max(m.trabajadas, m.trabajadas_anterior || 0)), 1,
  );

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <div>
          <h1>Panel</h1>
          <p className="panel-sub">
            Ofertas, pedidos y horas del año. Los pedidos y los partes importados del Access también cuentan.
          </p>
        </div>
        <div className="panel-filtros">
          <select value={clienteHash} onChange={(e) => setClienteHash(e.target.value)}>
            <option value="">Todos los clientes</option>
            {clientes.map((c) => <option key={c.hash} value={c.hash}>{c.name}</option>)}
          </select>
          <select value={anio} onChange={(e) => setAnio(Number(e.target.value))}>
            {[0, 1, 2, 3, 4].map((i) => (
              <option key={i} value={anioActual - i}>{anioActual - i}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="panel-kpis">
        <div className="panel-kpi">
          <small>Ofertas enviadas</small>
          <b>{r.ofertas_enviadas}</b>
          <em>{euros(r.importe_ofertado)} ofertados</em>
        </div>
        <div className="panel-kpi">
          <small>Con pedido</small>
          <b>{r.con_pedido}</b>
          <em>{r.aceptacion === null ? 'Sin ofertas enviadas' : `${String(r.aceptacion).replace('.', ',')} % de aceptación`}</em>
        </div>
        <div className="panel-kpi">
          <small>Ofertado vs pedido</small>
          <b>{porcentaje(r.desviacion)}</b>
          <em>{r.desviacion === null ? 'Sin pedidos con los dos precios' : 'desviación'}</em>
        </div>
        <div className="panel-kpi">
          <small>Horas trabajadas</small>
          <b>{horas(r.horas_trabajadas)}</b>
          <em>{horas(r.horas_a_facturar)} a facturar</em>
        </div>
      </div>

      <div className="panel-columnas">
        <div className="panel-tarjeta">
          <h2>Ofertas por cliente</h2>
          {datos.clientes.length === 0 ? (
            <p className="panel-vacio">Todavía no hay ofertas enviadas en {datos.anio}.</p>
          ) : (
            <table className="panel-tabla">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th className="der">Enviadas</th>
                  <th className="der">Con pedido</th>
                  <th>Aceptación</th>
                </tr>
              </thead>
              <tbody>
                {datos.clientes.map((c) => (
                  <tr key={c.cliente_hash} onClick={() => navigate('/offers')} className="pinchable">
                    <td>{c.cliente}</td>
                    <td className="der">{c.enviadas}</td>
                    <td className="der">{c.con_pedido}</td>
                    <td>
                      <div className="panel-barra" title={`${c.aceptacion} %`}>
                        <i style={{ width: `${c.aceptacion}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="panel-tarjeta">
          <h2>Piezas más pedidas en {datos.anio}</h2>
          {datos.piezas.length === 0 ? (
            <p className="panel-vacio">Ningún pedido de {datos.anio} trae el código de la pieza.</p>
          ) : (
            <table className="panel-tabla">
              <thead>
                <tr>
                  <th>Artículo</th>
                  <th className="der">Pedidos</th>
                  <th className="der">Horas</th>
                </tr>
              </thead>
              <tbody>
                {datos.piezas.map((p) => (
                  <tr key={p.codigo} className="pinchable"
                      onClick={() => navigate(`/offers/history?codigo=${encodeURIComponent(p.codigo)}`)}>
                    <td>{p.codigo}</td>
                    <td className="der">{p.pedidos}</td>
                    <td className="der">{horasFinas(p.horas)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="panel-nota">
            Las horas solo se suman cuando el pedido trae una sola pieza: en uno con varias, el
            tiempo no se puede repartir. Pincha en una para ver su histórico.
          </p>
        </div>
      </div>

      <div className="panel-tarjeta panel-ancho">
          <h2>Horas trabajadas y a facturar por mes</h2>
          <div className="panel-barras">
            {datos.meses.map((m) => (
              <div key={m.mes} className="panel-mes"
                   title={`${horas(m.trabajadas)} trabajadas · ${horas(m.a_facturar)} a facturar · ${horas(m.trabajadas_anterior)} en ${datos.anio - 1}`}>
                <div className="panel-mes-barras">
                  <i className="anterior" style={{ height: `${((m.trabajadas_anterior || 0) / topHoras) * 100}%` }} />
                  <i style={{ height: `${(m.trabajadas / topHoras) * 100}%` }} />
                  <i className="facturar" style={{ height: `${(m.a_facturar / topHoras) * 100}%` }} />
                </div>
                <span>{MESES[m.mes - 1]}</span>
              </div>
            ))}
          </div>
          <p className="panel-leyenda">
            <span className="anterior" /> {datos.anio - 1}
            <span className="trabajadas" /> Trabajadas
            <span className="facturar" /> A facturar
          </p>
          <p className="panel-nota">
            Los partes importados del Access no distinguen los dos tiempos: cuentan como que se
            factura todo lo trabajado. La diferencia solo aparece en los partes apuntados aquí.
          </p>
      </div>
    </div>
  );
};

export default DashboardPage;
